// Row shape for the sentinel `flags` table (migrations 0001 + 0010).
// The panel-side vocabulary (KIND_LABEL, SEVERITY_COLOR, ordering,
// aggregation helpers) lives in lib/reviewItems.ts since Item 2 of the
// 2026-05-14 architecture extension; this file is now the canonical
// home for the row shape only.
//
// One row per suspect span produced by backend/app/sentinel.py +
// backend/prompts/sentinel.md. Field shape pinned by the DB check
// constraints on `kind` and `severity` columns; see DECISIONS.md
// 2026-05-13 "Hallucination sentinel" entry.

export type FlagKind =
  | "proper_noun_oov"
  | "lost_thread"
  | "silence_fill"
  | "code_switch_break"
  | "loop_repetition"
  // Added 2026-05-17 with the role-inversion detector slice. New row
  // kind written by backend/app/role_inversion.py; migration 0013
  // extends the flags table's kind check constraint. See DECISIONS.md
  // 2026-05-17 "Role-inversion detector" entry.
  | "role_inversion"
  // Added 2026-05-25 with the attribution_critique pass. New row kind
  // written by backend/app/attribution_critique.py; migration 0019
  // extends the flags table's kind check constraint.
  | "attribution_second_look"
  // Added 2026-05-29 (Group B). Sentinel flags passages whose content is
  // clearly discordant with the meeting context — the overlap-confusion
  // signature where Soniox picks up a second voice. Migration 0023
  // extends the flags table's kind check constraint. User-facing label:
  // "May not fit the conversation". See DECISIONS.md 2026-05-29
  // "Group B — sentinel out_of_context_passage" entry.
  | "out_of_context_passage";

export type FlagSeverity = "low" | "medium" | "high";

export type FlagStatus = "pending" | "accepted" | "corrected" | "dismissed";

export interface Flag {
  id: string;
  meeting_id: string;
  kind: FlagKind;
  severity: FlagSeverity;
  start_ms: number;
  end_ms: number;
  reason: string;
  status: FlagStatus;
  evidence: Record<string, unknown>;
  created_at: string;
}

// ---------------------------------------------------------------------------
// Derived boolean: does any pending flag overlap a transcript paragraph?
//
// A flag overlaps a paragraph turn when the flag's [start_ms, end_ms) range
// intersects the turn's [turnStart, turnEnd) range. The overlap test is the
// standard half-open-interval predicate: flag starts before turn ends AND
// flag ends after turn starts.
//
// Used by TranscriptView to compute `hasPendingFlag` per turn in the parent
// map — the boolean is passed to TurnRow as a prop so React.memo skips
// unchanged rows. Pure function, no side-effects; safe to call per-turn on
// every render (flag list is small, typically < 20 items per meeting).
// ---------------------------------------------------------------------------
export function hasPendingFlagForTurn(
  flags: Flag[],
  turnStart: number,
  turnEnd: number,
): boolean {
  return flags.some(
    (f) =>
      f.status === "pending" &&
      f.start_ms < turnEnd &&
      f.end_ms > turnStart,
  );
}
